type EventMap = Record<string, any>;
type EventKey<T extends EventMap> = string & keyof T;

export type EventReceiver<T> = (params: T) => void;

export class EventBus<T extends EventMap> {
  private readonly logger: ((...args: any) => any) | null = null;
  private readonly events: Map<EventKey<T>, Set<(p: EventMap[keyof EventMap]) => void>> = new Map();

  constructor(logger?: (...args: any) => any) {
    this.logger = logger ?? null;
  }

  publish = <K extends EventKey<T>>(eventName: K, params: T[K]): void => {
    const handlers = this.getEventHandlers<K>(eventName);

    this.logger?.('event: ', 'publish', '\neventName: ', eventName, '\nparams: ', params, '\nhandlers: ', handlers);

    handlers.forEach((handler) => {
      handler(params);
    });
  };

  subscribe = <K extends EventKey<T>>(event: K, handler: EventReceiver<T[K]>): VoidFunction => {
    const handlers = this.getEventHandlers<K>(event);

    handlers.add(handler);

    this.logger?.('event: ', 'subscribe', '\nevent: ', event, '\nhandler: ', handler);

    return () => {
      this.unsubscribe(event, handler);
    };
  };

  unsubscribe = <K extends EventKey<T>>(event: K, handler: EventReceiver<T[K]>): void => {
    const handlers = this.getEventHandlers<K>(event);

    this.logger?.('event: ', 'unsubscribe', '\nevent: ', event, '\nhandler: ', handler);

    if (handlers.size === 0) {
      handlers.clear();
      return;
    }

    handlers.delete(handler);
  };

  private getEventHandlers<K extends EventKey<T>>(event: K): Set<(p: EventMap[keyof EventMap]) => void> {
    let set = this.events.get(event);

    if (!set) {
      set = new Set<(p: EventMap[keyof EventMap]) => void>();
      this.events.set(event, set);
    }

    return set;
  }
}
